/**
 * The Models item column (models.md §12 "List", design fix 6): the card "Use the recommended set-up" when
 * the set-up differs from it; one item per model in model order — icon, name, the two measured numbers,
 * where it runs, the accuracy and speed bars and its status on this device; then every scoring rule in
 * evaluation order under "Actions on scoring", each marked with the model it tests; then "Written
 * assessments" → the connected assistant. Search and "Enabled items only" filter it like any list.
 */

import { useNavigate } from 'react-router-dom';
import { reportError } from '../../app/errors.js';
import { showToast } from '../../app/Toast.js';
import { ui, useUi } from '../../app/ui-store.js';
import { useEngine } from '../../engine-client/EngineProvider.js';
import { Icon } from '../../icons/Icon.js';
import { Count } from '../../ui/Count.js';
import { Tool } from '../../ui/Tool.js';
import { useScoringRules } from '../rules/data.js';
import { filterItems, type ItemListProps, type SettingsItem } from '../shell/index.js';
import { accuracyValue, speedValue } from './catalog.js';
import { entryOf, useCatalog, useModels } from './data.js';
import { Meter } from './Meter.js';
import { isRecommendedSetUp } from './run.js';

export function RecommendedCard() {
  const engine = useEngine();
  const { models } = useModels();
  const catalog = useCatalog();
  const { rules, isLoading } = useScoringRules();
  if (isLoading || !catalog || models.length === 0 || isRecommendedSetUp(models, catalog, rules)) return null;
  const apply = async (): Promise<void> => {
    try {
      const result = await engine.models.applyRecommended();
      showToast('Recommended set-up applied', result.undoId);
    } catch (error) {
      reportError(error);
    }
  };
  return (
    <div className="model-recommend-card" data-recommended="false">
      <span className="model-recommend-icon">
        <Icon name="sparkles" />
      </span>
      <span>
        <strong>Use the recommended set-up</strong>
        <small>The on-device model sorts everything that reaches Inbox; Jev gives a second opinion on Priority and reads Cyrillic messages (Pro). Undo puts your set-up back.</small>
      </span>
      <button type="button" className="secondary-button" data-action="apply-recommended" onClick={() => void apply()}>
        Use it
      </button>
    </div>
  );
}

export function ModelsList({ category, items, isLoading, selectedId }: ItemListProps) {
  const navigate = useNavigate();
  const search = useUi((s) => s.settingsSearch);
  const enabledOnly = useUi((s) => s.settingsEnabledOnly);
  const catalog = useCatalog();
  const shown = filterItems(items, search, enabledOnly);
  const open = (r: SettingsItem): void => navigate(`/settings/${category.id}/${encodeURIComponent(r.id)}`);
  return (
    <section className="settings-list models-list">
      <div className="pane-heading">
        <span>
          <Tool icon="menu" label="Settings categories" onClick={() => ui.set({ mobileOverlay: 'navigation' })} /> {category.name} <Count value={shown.length} />
        </span>
        {category.newLabel ? <Tool icon="plus" label="Add item" onClick={() => navigate(`/settings/${category.id}/new`)} /> : null}
      </div>
      <div className="setting-items">
        <RecommendedCard />
        {shown.length ? (
          shown.flatMap((r) => {
            const entry = entryOf(catalog, r.id);
            const selected = selectedId === r.id;
            const heading = r.groupHeading ? [<h3 key={`h:${r.id}`} className="setting-group-heading">{r.groupHeading}</h3>] : [];
            if (!entry) {
              return [
                ...heading,
                <button type="button" key={r.id} className={selected ? 'setting-item selected' : 'setting-item'} aria-current={selected ? 'true' : undefined} data-item-id={r.id} onClick={() => open(r)}>
                  <span className="setting-item-icon">
                    <Icon name={r.icon} />
                  </span>
                  <span>
                    <strong>{r.name}</strong>
                    {r.subtitle ? <small>{r.subtitle}</small> : null}
                  </span>
                  {r.enabled !== undefined ? <i className={r.enabled ? 'item-status on' : 'item-status'} title={r.enabled ? 'Enabled' : 'Disabled'} /> : null}
                </button>,
              ];
            }
            return [
              ...heading,
              <button
                type="button"
                key={r.id}
                className={selected ? 'setting-item model-item selected' : 'setting-item model-item'}
                aria-current={selected ? 'true' : undefined}
                data-item-id={r.id}
                data-model-item={r.id}
                onClick={() => open(r)}
              >
                <span className="setting-item-icon">
                  <Icon name={r.icon} />
                </span>
                <span>
                  <strong>{r.name}</strong>
                  {(r.subtitle ?? '').split('\n').filter(Boolean).map((line) => (
                    <small key={line}>{line}</small>
                  ))}
                </span>
                <i className={['item-status', r.enabled ? 'on' : '', r.tone ? `tone-${r.tone}` : ''].filter(Boolean).join(' ')} title={r.status ?? (r.enabled ? 'Active' : 'Off')} />
                <span className="model-item-foot">
                  <span className="model-meters" aria-label="Accuracy and speed">
                    <Meter label="accuracy" value={accuracyValue(entry)} />
                    <Meter label="speed" value={speedValue(entry)} />
                  </span>
                  <span className="model-item-status" data-model-status={r.id}>
                    {r.status}
                  </span>
                </span>
              </button>,
            ];
          })
        ) : (
          <div className="empty">
            <h2>{isLoading ? 'Loading…' : 'No items'}</h2>
          </div>
        )}
      </div>
    </section>
  );
}
